/**
 * AcademicContext.jsx
 *
 * Single source of truth for "which Academic Year / Semester am I looking at?"
 * inside a school tenant. A semester is an independent container — switching
 * the selector re-scopes every academic module without ever deleting or
 * hiding another semester's records.
 *
 * - `useAcademicContext(tenantId)` → { yearId, termId, setYearId, setTermId,
 *      years, year, semesters, term, structure }
 *   Persisted per tenant in localStorage.
 * - `<AcademicContextBar tenantId={...} />` — the switcher, mounted on every
 *   school page, with live per-semester record counts.
 *
 * Powered by GET /api/school/tenants/{tenantId}/academic-structure
 */
import React, { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { authHeaders } from '@/lib/authToken';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { CalendarRange, Layers, BookOpen, Users, Clock, Receipt, ClipboardCheck, Trophy } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fetchJson = async (path) => {
  const res = await fetch(`${API_URL}${path}`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Request failed');
  return res.json();
};

const yearKey = (t) => `school:acadyear:${t}`;
const termKey = (t) => `school:acadterm:${t}`;
const read = (k) => { try { return localStorage.getItem(k) || ''; } catch (_e) { return ''; } };
const write = (k, v) => { try { v ? localStorage.setItem(k, v) : localStorage.removeItem(k); } catch (_e) { /* noop */ } };

export const useAcademicContext = (tenantId) => {
  const [yearId, _setYearId] = React.useState(() => (tenantId ? read(yearKey(tenantId)) : ''));
  const [termId, _setTermId] = React.useState(() => (tenantId ? read(termKey(tenantId)) : ''));

  const { data: structure } = useQuery({
    queryKey: ['school-academic-structure', tenantId],
    queryFn: () => fetchJson(`/api/school/tenants/${tenantId}/academic-structure`),
    enabled: !!tenantId,
    refetchOnWindowFocus: false,
  });

  const years = useMemo(() => structure?.years || [], [structure]);
  const year = useMemo(() => years.find((y) => y.id === yearId) || null, [years, yearId]);
  const semesters = useMemo(() => year?.semesters || [], [year]);
  const term = useMemo(() => semesters.find((s) => s.id === termId) || null, [semesters, termId]);

  const setYearId = (v) => { _setYearId(v); write(yearKey(tenantId), v); _setTermId(''); write(termKey(tenantId), ''); };
  const setTermId = (v) => { _setTermId(v); write(termKey(tenantId), v); };

  // Seed / repair the selection: prefer the current year, then its current
  // semester, then the first one. Never leave a stale id selected.
  useEffect(() => {
    if (years.length === 0) return;
    const validYear = years.find((y) => y.id === yearId);
    if (!validYear) {
      // A tenant can hold several `is_current` years (one per campus), so prefer
      // a current year that actually contains semesters before falling back.
      const withSemesters = years.filter((y) => (y.semesters || []).length > 0);
      const next = withSemesters.find((y) => y.is_current)
        || withSemesters[0]
        || years.find((y) => y.is_current)
        || years[0];
      _setYearId(next.id); write(yearKey(tenantId), next.id);
      _setTermId(''); write(termKey(tenantId), '');
      return;
    }
    const list = validYear.semesters || [];
    if (list.length && !list.find((s) => s.id === termId)) {
      const nextTerm = list.find((s) => s.is_current) || list[0];
      _setTermId(nextTerm.id); write(termKey(tenantId), nextTerm.id);
    }
    if (!list.length && termId) { _setTermId(''); write(termKey(tenantId), ''); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [years, yearId, termId, tenantId]);

  return { yearId, termId, setYearId, setTermId, years, year, semesters, term, structure };
};

const COUNT_CHIPS = [
  { key: 'classes', label: 'classes', icon: Users },
  { key: 'timetable_slots', label: 'timetable', icon: Clock },
  { key: 'fee_structures', label: 'fee structures', icon: Receipt },
  { key: 'fee_charges', label: 'fee charges', icon: Receipt },
  { key: 'exams', label: 'exams', icon: BookOpen },
  { key: 'attendance_sessions', label: 'attendance', icon: ClipboardCheck },
  { key: 'results', label: 'results', icon: Trophy },
];

export const AcademicContextBar = ({ tenantId }) => {
  const { yearId, termId, setYearId, setTermId, years, year, semesters, term } =
    useAcademicContext(tenantId);

  if (years.length === 0) return null;

  return (
    <div
      className="flex items-center gap-2 flex-wrap rounded-lg border bg-white px-3 py-2 mb-4 text-xs shadow-sm"
      data-testid="academic-context-bar"
    >
      <CalendarRange className="h-3.5 w-3.5 text-indigo-600" />
      <Select value={yearId} onValueChange={setYearId}>
        <SelectTrigger className="h-7 w-52 text-xs" data-testid="academic-year-select">
          <SelectValue placeholder="Academic year" />
        </SelectTrigger>
        <SelectContent>
          {years.map((y) => (
            <SelectItem key={y.id} value={y.id}>
              {y.name}{y.is_current ? ' · current' : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Layers className="h-3.5 w-3.5 text-violet-600" />
      {semesters.length > 0 ? (
        <Select value={termId} onValueChange={setTermId}>
          <SelectTrigger className="h-7 w-52 text-xs" data-testid="semester-select">
            <SelectValue placeholder="Semester" />
          </SelectTrigger>
          <SelectContent>
            {semesters.map((s) => (
              <SelectItem key={s.id} value={s.id} data-testid={`semester-option-${s.id}`}>
                {s.name}{s.is_current ? ' · current' : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className="text-amber-600" data-testid="semester-none">
          No semesters in {year?.name || 'this year'} yet — add one under Academic Years
        </span>
      )}

      {term && (
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="semester-counts">
          {COUNT_CHIPS.map(({ key, label, icon: Icon }) => (
            <Badge key={key} variant="outline" className="text-[10px] gap-1 font-normal"
                   data-testid={`semester-count-${key}`}>
              <Icon className="h-3 w-3" />
              {term.counts?.[key] ?? 0} {label}
            </Badge>
          ))}
        </div>
      )}

      {semesters.length > 1 && (
        <span className="ml-auto text-slate-400 hidden lg:inline">
          {semesters.length} semesters — switching never hides another
        </span>
      )}
    </div>
  );
};

export default AcademicContextBar;
